import {
  Alert,
  buildPath,
  buildTemplatePath,
  Card,
  Form,
  newToast,
  PageRoute,
  pn,
  Stack,
  Typography,
  useParams,
  useTheme,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import { ResetPassword, ResetPasswordViewResource } from "generated/openApi";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { the404PageRoute } from "pages/The404Page/The404Page";
import { loginPageRoute } from "../LoginPage/LoginPage";
import { AuthField } from "../components/AuthField";
import { AuthPageWrapper } from "../components/AuthPageWrapper";
import { pillButtonStyles } from "../styles/authStyles";
import { loginPageV2Route } from "pages/authV2/LoginPageV2";

const routeName = "resetPasswordv2";

class Params {
  userId: number | undefined = undefined;
  validationToken: string | undefined = undefined;
}

export const resetPasswordV2PageRoute: PageRoute<Params> = {
  handle: "Reset Password",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const ResetPasswordV2Page = () => {
  const { params } = useParams(routeName, Params);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [resetPassword, setResetPassword] = useState<ResetPassword>({} as ResetPassword);
  const [viewResource, setViewResource] = useState<ResetPasswordViewResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  const properties = viewResource.properties || [];

  const handleFieldChange = (name: string, value: string) => {
    setResetPassword((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    setValidationErrors({});

    ApiService.authV2
      .getResetPassword(params.userId, params.validationToken)
      .then(setResetPassword)
      .catch(() => navigate({ pathname: `/${the404PageRoute.getPath()}` }));

    ApiService.auth.getResetPasswordViewResource().then(setViewResource);
  }, []);

  const post = () => {
    setValidationErrors({});

    ApiService.authV2
      .postResetPassword(resetPassword)
      .then(() => {
        dispatch(setAppToast(newToast("success")));
        navigate({ pathname: `/${loginPageV2Route.getPath()}` });
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const generalErrors = validationErrors?.[pn<ResetPassword>("generalValidationErrors")];

  return (
    <AuthPageWrapper>
      <Card sx={{ maxWidth: "550px", width: "100%" }}>
        <Stack px={3} py={7} spacing="lg">
          <Stack spacing="sm" alignItems="center">
            <Typography variant="h4" fontWeight={theme.typography.fontWeightMedium}>
              Reset Your Password
            </Typography>
          </Stack>

          <Form>
            <Stack spacing="md">
              {generalErrors && <Alert severity="error">{generalErrors[0]}</Alert>}
              <AuthField
                name="newPassword"
                value={resetPassword.newPassword || ""}
                onChange={handleFieldChange}
                properties={properties}
                validationErrors={validationErrors}
                type="password"
              />
              <AuthField
                name="confirmNewPassword"
                value={resetPassword.confirmNewPassword || ""}
                onChange={handleFieldChange}
                properties={properties}
                validationErrors={validationErrors}
                type="password"
              />

              <SubmitButton
                onClick={post}
                label="Reset Password"
                fullWidth
                sx={{ height: "50px", ...pillButtonStyles }}
              />
            </Stack>
          </Form>
        </Stack>
      </Card>
    </AuthPageWrapper>
  );
};